import React, { createContext, useState, useEffect } from 'react';
import { useBusinessAccounts, BusinessAccount } from '@/hooks/useBusinessAccounts';

export interface BusinessAccountContextType {
  selectedAccountId: string | null;
  selectedAccount: BusinessAccount | null;
  setSelectedAccountId: (accountId: string) => void;
  clearSelectedAccount: () => void;
  accounts: BusinessAccount[] | null;
  isLoading: boolean;
}

// Trust whatever id shape the backend gives us. The previous strict UUID
// regex silently dropped legitimate ids that didn't match RFC 4122 hex
// (e.g. ids ending in non-hex chars), causing refresh to lose the user's
// selection and revert to accounts[0]. Validation against the actual
// accounts list happens implicitly via `selectedAccount = find(...) || null`.
const isValidStoredId = (v: string | null) => typeof v === 'string' && v.trim().length > 0;

const globalKey = '__BUSINESS_ACCOUNT_CONTEXT__';
const globalObject = typeof globalThis !== 'undefined' ? (globalThis as any) : {};

export const BusinessAccountContext: React.Context<BusinessAccountContextType | undefined> =
  globalObject[globalKey] || createContext<BusinessAccountContextType | undefined>(undefined);

if (globalObject && !globalObject[globalKey]) {
  globalObject[globalKey] = BusinessAccountContext;
}

export const BusinessAccountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: accounts, isLoading, isPending } = useBusinessAccounts();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('selectedBusinessAccountId');
      if (isValidStoredId(stored)) return stored;
    }
    return null;
  });

  useEffect(() => {
    if (selectedAccountId && typeof window !== 'undefined') {
      localStorage.setItem('selectedBusinessAccountId', selectedAccountId);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    if (accounts && accounts.length > 0 && !selectedAccountId) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, selectedAccountId, setSelectedAccountId]);

  // Debug logging
  useEffect(() => {
  }, [accounts, selectedAccountId]);

  // Resolve the effective selection DURING RENDER rather than waiting for the
  // auto-select effect below to commit. Without this there is a one-render window
  // where `accounts` has arrived but `selectedAccountId` is still null, and every
  // consumer that branches on `!selectedAccountId` paints its "No Business
  // Account" empty state for a frame — on a slow API (prod Lambda cold start)
  // that reads as a real flash of "Connect your Meta Business account" on /home
  // before the dashboard appears. The value is identical to what the effect sets,
  // so this is purely a timing fix; a stored id is still trusted verbatim and is
  // never overridden by accounts[0].
  const resolvedAccountId = selectedAccountId ?? accounts?.[0]?.id ?? null;

  const selectedAccount = accounts?.find(account => account.id === resolvedAccountId) || null;

  const clearSelectedAccount = () => {
    setSelectedAccountId(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('selectedBusinessAccountId');
    }
  };

  const value: BusinessAccountContextType = {
    selectedAccountId: resolvedAccountId,
    selectedAccount,
    setSelectedAccountId,
    clearSelectedAccount,
    accounts: accounts || null,
    // `useBusinessAccounts` is gated on `enabled: !!user`, and React Query v5
    // reports `isLoading === false` while a query is disabled. So during auth
    // bootstrap (and on any first fetch) we had accounts=undefined AND
    // isLoading=false — consumers concluded "no business account" and rendered
    // the connect CTA. `isPending` stays true until data actually arrives, which
    // is the honest "we don't know yet" signal. An errored query still resolves
    // to false, so genuine failures keep their existing empty-state behavior.
    isLoading: Boolean(isPending) || isLoading,
  };

  return (
    <BusinessAccountContext.Provider value={value}>
      {children}
    </BusinessAccountContext.Provider>
  );
};

export default BusinessAccountProvider;
